import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { DocumentNode, useQuery } from '@apollo/client'

import {
  PaginationSource,
  ROWS_PER_PAGE_OPTIONS,
  useDataGrid,
  useDataGridPagination2,
} from 'shared/hooks'
import { useQueryParams2 } from 'shared/hooks/use-query-params'
import { PaginationCursor } from 'shared/hooks/use-data-grid-pagination'
import { URL_QUERY_PARAMS } from 'shared/constants/queryParams'
import { PageInfo2 } from 'shared/types'
import {
  AdminRow,
  AdminsConnection,
  AdminsPaginationVariables,
  AdminsScopeVariables,
} from '../types'

interface BuildAdminsPaginationVariablesArgs {
  pageSize: number
  cursor: PaginationCursor
  nameOrEmail: string
}

export const buildAdminsPaginationVariables = ({
  pageSize,
  cursor,
  nameOrEmail,
}: BuildAdminsPaginationVariablesArgs): AdminsPaginationVariables => {
  const normalizedQuery = nameOrEmail.trim()
  const nameOrEmailVariable = normalizedQuery && {
    nameOrEmail: normalizedQuery,
  }

  if (cursor.before !== undefined) {
    return { before: cursor.before, last: pageSize, ...nameOrEmailVariable }
  }

  return {
    first: pageSize,
    ...(cursor.after !== undefined && { after: cursor.after }),
    ...nameOrEmailVariable,
  }
}

interface PaginationSnapshot {
  loading: boolean
  totalCount?: number
  pageInfo: PageInfo2
}

const takePaginationSnapshot = <TEdge>(
  loading: boolean,
  admins: AdminsConnection<TEdge> | undefined,
  previous: PaginationSnapshot
): PaginationSnapshot => {
  if (!admins) return { ...previous, loading }

  return {
    loading,
    totalCount: admins.totalCount,
    pageInfo: {
      startCursor: admins.pageInfo.startCursor ?? null,
      endCursor: admins.pageInfo.endCursor ?? null,
      hasNextPage: Boolean(admins.pageInfo.hasNextPage),
      hasPreviousPage: Boolean(admins.pageInfo.hasPreviousPage),
    },
  }
}

const isSameSnapshot = (a: PaginationSnapshot, b: PaginationSnapshot) =>
  a.loading === b.loading &&
  a.totalCount === b.totalCount &&
  a.pageInfo.startCursor === b.pageInfo.startCursor &&
  a.pageInfo.endCursor === b.pageInfo.endCursor &&
  a.pageInfo.hasNextPage === b.pageInfo.hasNextPage &&
  a.pageInfo.hasPreviousPage === b.pageInfo.hasPreviousPage

interface UseAdminsTableArgs<TData, TEdge> {
  adminsQuery: DocumentNode
  scopeVariables: AdminsScopeVariables
  selectAdmins: (data: TData) => AdminsConnection<TEdge> | null | undefined
  mapEdgeToRow: (edge: TEdge) => AdminRow
  nameOrEmail?: string
  paginationSource: PaginationSource
}

export const useAdminsTable = <TData, TEdge>({
  adminsQuery,
  scopeVariables,
  selectAdmins,
  mapEdgeToRow,
  nameOrEmail = '',
  paginationSource,
}: UseAdminsTableArgs<TData, TEdge>) => {
  const { t } = useTranslation()
  const { getParam } = useQueryParams2()

  const [paginationSnapshot, setPaginationSnapshot] =
    useState<PaginationSnapshot>(() => ({
      loading: false,
      pageInfo: {
        startCursor: getParam(URL_QUERY_PARAMS.START_CURSOR) ?? null,
        endCursor: getParam(URL_QUERY_PARAMS.END_CURSOR) ?? null,
        hasNextPage: true,
        hasPreviousPage: true,
      },
    }))

  const {
    pageSize,
    setPageSize,
    page,
    handlePageChange,
    cursor,
    rowCountState,
  } = useDataGridPagination2(
    paginationSnapshot.loading,
    paginationSnapshot.totalCount,
    paginationSnapshot.pageInfo,
    paginationSource,
    0
  )

  const { data, previousData, loading, error, refetch } = useQuery<TData>(
    adminsQuery,
    {
      variables: {
        ...scopeVariables,
        ...buildAdminsPaginationVariables({ pageSize, cursor, nameOrEmail }),
      },
      fetchPolicy: 'no-cache',
      notifyOnNetworkStatusChange: true,
    }
  )

  const admins =
    (data && selectAdmins(data)) ??
    (previousData && selectAdmins(previousData)) ??
    undefined

  // useDataGridPagination2 needs the response before the query runs, so the
  // snapshot is updated during render and React re-renders before committing.
  const nextPaginationSnapshot = takePaginationSnapshot(
    loading,
    admins,
    paginationSnapshot
  )
  if (!isSameSnapshot(paginationSnapshot, nextPaginationSnapshot)) {
    setPaginationSnapshot(nextPaginationSnapshot)
  }

  const isCurrentPageEmpty =
    !loading && admins !== undefined && admins.edges.length === 0 && page > 0

  useEffect(() => {
    if (isCurrentPageEmpty) handlePageChange(0)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCurrentPageEmpty])

  const rows = useMemo<AdminRow[]>(
    () => admins?.edges.map(mapEdgeToRow) ?? [],
    [admins?.edges, mapEdgeToRow]
  )

  const { dataGridProps } = useDataGrid(
    error,
    pageSize,
    setPageSize,
    ROWS_PER_PAGE_OPTIONS,
    rowCountState,
    page,
    handlePageChange,
    null,
    null,
    loading
  )

  const localeText = useMemo(
    () => ({
      ...dataGridProps.localeText,
      MuiTablePagination: {
        labelRowsPerPage: t('rowsPerPage'),
        labelDisplayedRows: ({
          from,
          to,
          count,
        }: {
          from: number
          to: number
          count: number
        }) => t('displayedRows', { from, to, count }),
      },
    }),
    [dataGridProps.localeText, t]
  )

  return {
    rows,
    isLoading: loading,
    refetch,
    dataGridProps: {
      ...dataGridProps,
      localeText,
    },
  }
}
